import React, { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
    MdCheckCircle,
    MdClose,
    MdCloudDownload,
    MdEvent,
    MdGroups,
    MdRefresh,
    MdSearch,
    MdSportsSoccer,
} from 'react-icons/md';
import useApi from '../hooks/useApi';
import { userState } from '../store';
import { Menu } from '../ui/layout/Menu';
import { FooterCmp } from '../ui/layout/Footer';
import { PageHeader } from '../ui/ds/PageHeader';
import { Card } from '../ui/ds/Card';
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';
import { Input } from '../ui/ds/Input';
import { AdminSubmenu } from './AdminSubmenu';

type SportsDbLeague = Record<string, any>;
type ImportResult = Record<string, any> | null;

const getLeagueId = (league: SportsDbLeague): number | null => {
    const raw = league.idLeague ?? league.leagueId ?? league.id;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
};

const getLeagueName = (league: SportsDbLeague) =>
    league.strLeague ?? league.name ?? league.leagueName ?? `Liga ${getLeagueId(league) ?? ''}`.trim();

const getLeagueSport = (league: SportsDbLeague) =>
    league.strSport ?? league.sport ?? league.type ?? '';

const getLeagueCountry = (league: SportsDbLeague) =>
    league.strCountry ?? league.country ?? league.strNation ?? '';

const getLeagueBadge = (league: SportsDbLeague) =>
    league.strBadge ?? league.badge ?? league.logo ?? league.strLogo ?? '';

const getTournamentId = (result: ImportResult): number | null => {
    if (!result) return null;
    const tournament = result.tournament ?? result;
    const raw = tournament.id ?? tournament.tournamentId;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
};

const formatResult = (result: ImportResult) => {
    if (!result) return '';
    return JSON.stringify(result, null, 2);
};

export const AdminImport = () => {
    const userSnap = useSnapshot(userState);
    const { backend } = useApi();

    const [leagues, setLeagues] = useState<SportsDbLeague[]>([]);
    const [loadingLeagues, setLoadingLeagues] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(null);
    const [seasons, setSeasons] = useState<string[]>([]);
    const [loadingSeasons, setLoadingSeasons] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState('');

    const [tournamentName, setTournamentName] = useState('');
    const [showTournament, setShowTournament] = useState(true);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [tournamentId, setTournamentId] = useState('');
    const [updateExisting, setUpdateExisting] = useState(true);

    const [workingAction, setWorkingAction] = useState<'tournament' | 'teams' | 'matches' | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastResult, setLastResult] = useState<ImportResult>(null);

    const selectedLeague = useMemo(
        () => leagues.find(league => getLeagueId(league) === selectedLeagueId),
        [leagues, selectedLeagueId],
    );

    const localTournamentId = Number(tournamentId);
    const canUseTournamentId = Number.isFinite(localTournamentId) && localTournamentId > 0;

    const clearFeedback = () => {
        setMessage(null);
        setError(null);
    };

    const showError = (e: any, fallback: string) => {
        setError(e?.body?.message ?? e?.message ?? fallback);
        setMessage(null);
    };

    const loadLeagues = async (searchQuery = query) => {
        setLoadingLeagues(true);
        clearFeedback();
        try {
            const trimmed = searchQuery.trim();
            const loaded = trimmed
                ? await backend.adminImport.importerControllerAdminSearchSportsDbLeagues(trimmed)
                : await backend.adminImport.importerControllerAdminListSportsDbLeagues();
            setLeagues(loaded ?? []);
            if (loaded?.length) {
                const firstId = getLeagueId(loaded[0]);
                setSelectedLeagueId(current =>
                    loaded.some(league => getLeagueId(league) === current) ? current : firstId
                );
            } else {
                setSelectedLeagueId(null);
            }
        } catch (e: any) {
            showError(e, 'Ligy se nepodařilo načíst.');
        } finally {
            setLoadingLeagues(false);
        }
    };

    useEffect(() => {
        if (userSnap.isLoggedIn && userSnap.userProfile.role === 'Admin') {
            loadLeagues('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSnap.isLoggedIn, userSnap.userProfile.role]);

    useEffect(() => {
        if (!selectedLeagueId) {
            setSeasons([]);
            setSelectedSeason('');
            return;
        }

        setLoadingSeasons(true);
        backend.adminImport.importerControllerAdminListSportsDbSeasons(selectedLeagueId)
            .then(loaded => {
                setSeasons(loaded ?? []);
                setSelectedSeason(loaded?.[0] || '');
            })
            .catch(e => showError(e, 'Sezóny se nepodařilo načíst.'))
            .finally(() => setLoadingSeasons(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLeagueId]);

    useEffect(() => {
        if (selectedLeague && !tournamentName) {
            setTournamentName(getLeagueName(selectedLeague));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLeagueId]);

    const createTournament = async () => {
        if (!selectedLeagueId || !selectedSeason) return;
        setWorkingAction('tournament');
        clearFeedback();
        try {
            const result = await backend.adminImport.importerControllerAdminImportSportsDbTournament({
                leagueId: selectedLeagueId,
                season: selectedSeason,
                name: tournamentName.trim() || undefined,
                show: showTournament,
                dateStart: dateStart || undefined,
                dateEnd: dateEnd || undefined,
            });
            const createdTournamentId = getTournamentId(result);
            if (createdTournamentId) setTournamentId(String(createdTournamentId));
            setLastResult(result);
            setMessage(createdTournamentId
                ? `Turnaj byl připraven. ID turnaje: ${createdTournamentId}.`
                : 'Turnaj byl připraven.');
        } catch (e: any) {
            showError(e, 'Turnaj se nepodařilo vytvořit.');
        } finally {
            setWorkingAction(null);
        }
    };

    const importTeams = async () => {
        if (!selectedLeagueId || !canUseTournamentId) return;
        setWorkingAction('teams');
        clearFeedback();
        try {
            const result = await backend.adminImport.importerControllerAdminImportSportsDbTeams(localTournamentId);
            setLastResult(result);
            setMessage('Týmy byly importovány.');
        } catch (e: any) {
            showError(e, 'Týmy se nepodařilo importovat.');
        } finally {
            setWorkingAction(null);
        }
    };

    const importMatches = async () => {
        if (!selectedLeagueId || !selectedSeason || !canUseTournamentId) return;
        setWorkingAction('matches');
        clearFeedback();
        try {
            const result = await backend.adminImport.importerControllerAdminImportSportsDbMatches(localTournamentId, {
                season: selectedSeason,
                updateExisting,
            });
            setLastResult(result);
            setMessage('Zápasy byly importovány.');
        } catch (e: any) {
            showError(e, 'Zápasy se nepodařilo importovat.');
        } finally {
            setWorkingAction(null);
        }
    };

    if (!userSnap.isLoggedIn) {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🔒</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Pouze pro administrátory</p>
                    <p className="tb-body text-text-muted">Pro pokračování se přihlaste administrátorským účtem.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    if (userSnap.userProfile.role !== 'Admin') {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🚫</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Nemáte oprávnění</p>
                    <p className="tb-body text-text-muted">Tato stránka je dostupná pouze administrátorům.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    return (
        <>
            <Menu />
            <PageHeader
                title="Administrace"
                subtitle="Import dat z TheSportsDB"
                right={
                    <button
                        type="button"
                        onClick={() => loadLeagues(query)}
                        disabled={loadingLeagues}
                        title="Obnovit ligy"
                        className="w-10 h-10 flex items-center justify-center rounded-content bg-white/15 hover:bg-white/25 transition-colors text-white disabled:opacity-50"
                    >
                        <MdRefresh className={`text-[22px] ${loadingLeagues ? 'animate-spin' : ''}`} />
                    </button>
                }
            />
            <AdminSubmenu />

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-6">
                {(message || error) && (
                    <div className={[
                        'flex items-center gap-3 rounded-content px-4 py-3',
                        error
                            ? 'bg-semantic-danger/10 border border-semantic-danger/20'
                            : 'bg-semantic-success/10 border border-semantic-success/20',
                    ].join(' ')}>
                        <Badge variant={error ? 'danger' : 'success'}>
                            {error ? 'Chyba' : 'OK'}
                        </Badge>
                        <span className="tb-body text-text-primary">{error ?? message}</span>
                        <button
                            onClick={() => { setError(null); setMessage(null); }}
                            className="ml-auto text-text-muted hover:text-text-primary transition-colors"
                        >
                            <MdClose className="text-[18px]" />
                        </button>
                    </div>
                )}

                <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] gap-6 items-start">
                    <Card className="flex flex-col gap-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="tb-h3 text-text-primary">Zdrojová liga</p>
                                <p className="tb-caption">Vyberte ligu a sezónu z TheSportsDB.</p>
                            </div>
                            <Badge variant="info">TheSportsDB</Badge>
                        </div>

                        <form
                            className="flex flex-col sm:flex-row gap-3"
                            onSubmit={e => {
                                e.preventDefault();
                                loadLeagues(query);
                            }}
                        >
                            <div className="relative flex-1">
                                <MdSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-text-muted" />
                                <input
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Hledat ligu…"
                                    className="w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input pl-11 pr-4 text-[15px] font-semibold text-text-primary font-nunito placeholder:text-text-muted focus:border-brand-blue focus:outline-none transition-colors"
                                />
                            </div>
                            <Btn type="submit" variant="primary" size="sm" icon={<MdSearch />} disabled={loadingLeagues}>
                                {loadingLeagues ? 'Hledám…' : 'Hledat'}
                            </Btn>
                        </form>

                        <div className="border border-border-neutral rounded-content overflow-hidden">
                            <div className="max-h-[420px] overflow-y-auto scrollbar-thin">
                                {loadingLeagues && leagues.length === 0 ? (
                                    <div className="flex items-center justify-center py-16">
                                        <MdRefresh className="text-[32px] text-brand-blue animate-spin" />
                                    </div>
                                ) : leagues.length === 0 ? (
                                    <div className="px-5 py-12 text-center">
                                        <MdSportsSoccer className="mx-auto text-[42px] text-text-muted/40" />
                                        <p className="tb-h3 text-text-primary mt-3">Žádné ligy</p>
                                        <p className="tb-caption">Zkuste jiný vyhledávací dotaz.</p>
                                    </div>
                                ) : leagues.map(league => {
                                    const id = getLeagueId(league);
                                    const selected = id === selectedLeagueId;
                                    const badge = getLeagueBadge(league);
                                    return (
                                        <button
                                            key={id ?? getLeagueName(league)}
                                            type="button"
                                            onClick={() => {
                                                setSelectedLeagueId(id);
                                                setTournamentName(getLeagueName(league));
                                            }}
                                            disabled={!id}
                                            className={[
                                                'w-full px-4 py-3 flex items-center gap-3 text-left border-b border-divider-row last:border-b-0 transition-colors',
                                                selected ? 'bg-brand-blue/10' : 'hover:bg-bg-app',
                                                !id ? 'opacity-50 cursor-not-allowed' : '',
                                            ].join(' ')}
                                        >
                                            <div className="w-11 h-11 rounded-card bg-bg-inputSoft border border-border-neutral flex items-center justify-center overflow-hidden flex-shrink-0">
                                                {badge ? (
                                                    <img src={badge} alt="" className="w-full h-full object-contain p-1" />
                                                ) : (
                                                    <MdSportsSoccer className="text-[24px] text-text-muted" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="tb-h4 text-text-primary truncate">{getLeagueName(league)}</p>
                                                <p className="tb-caption truncate">
                                                    {[getLeagueSport(league), getLeagueCountry(league), id ? `ID ${id}` : 'Bez ID']
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </p>
                                            </div>
                                            {selected && <MdCheckCircle className="text-[22px] text-brand-blue flex-shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </Card>

                    <div className="flex flex-col gap-6">
                        <Card className="flex flex-col gap-4">
                            <div>
                                <p className="tb-h3 text-text-primary">Nastavení importu</p>
                                <p className="tb-caption">
                                    {selectedLeague ? getLeagueName(selectedLeague) : 'Nejdřív vyberte ligu.'}
                                </p>
                            </div>

                            <div>
                                <p className="tb-label mb-1.5">Sezóna</p>
                                <select
                                    value={selectedSeason}
                                    onChange={e => setSelectedSeason(e.target.value)}
                                    disabled={!selectedLeagueId || loadingSeasons}
                                    className="w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input px-4 text-[15px] font-semibold text-text-primary font-nunito focus:border-brand-blue focus:outline-none transition-colors disabled:bg-bg-inputSoft disabled:text-text-muted"
                                >
                                    <option value="">{loadingSeasons ? 'Načítám sezóny…' : 'Vyberte sezónu'}</option>
                                    {seasons.map(season => (
                                        <option key={season} value={season}>{season}</option>
                                    ))}
                                </select>
                            </div>

                            <Input
                                label="Název turnaje"
                                value={tournamentName}
                                onChange={e => setTournamentName(e.target.value)}
                                placeholder="Volitelný lokální název"
                            />

                            <div className="grid sm:grid-cols-2 gap-3">
                                <Input
                                    label="Datum od"
                                    type="date"
                                    value={dateStart}
                                    onChange={e => setDateStart(e.target.value)}
                                />
                                <Input
                                    label="Datum do"
                                    type="date"
                                    value={dateEnd}
                                    onChange={e => setDateEnd(e.target.value)}
                                />
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showTournament}
                                    onChange={e => setShowTournament(e.target.checked)}
                                    className="w-5 h-5 accent-brand-blue"
                                />
                                <span className="tb-body text-text-primary">Zobrazit turnaj v aplikaci</span>
                            </label>

                            <Btn
                                type="button"
                                variant="primary"
                                size="md"
                                fullWidth
                                icon={<MdCloudDownload />}
                                onClick={createTournament}
                                disabled={!selectedLeagueId || !selectedSeason || workingAction !== null}
                            >
                                {workingAction === 'tournament' ? 'Vytvářím…' : 'Vytvořit turnaj'}
                            </Btn>
                        </Card>

                        <Card className="flex flex-col gap-4">
                            <div>
                                <p className="tb-h3 text-text-primary">Import do turnaje</p>
                                <p className="tb-caption">Použije se ID vytvořeného nebo existujícího lokálního turnaje.</p>
                            </div>

                            <Input
                                label="ID turnaje"
                                type="number"
                                min={1}
                                value={tournamentId}
                                onChange={e => setTournamentId(e.target.value)}
                                placeholder="Např. 1"
                            />

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={updateExisting}
                                    onChange={e => setUpdateExisting(e.target.checked)}
                                    className="w-5 h-5 accent-brand-blue"
                                />
                                <span className="tb-body text-text-primary">Aktualizovat existující zápasy</span>
                            </label>

                            <div className="grid sm:grid-cols-2 gap-3">
                                <Btn
                                    type="button"
                                    variant="outline"
                                    size="md"
                                    fullWidth
                                    icon={<MdGroups />}
                                    onClick={importTeams}
                                    disabled={!selectedLeagueId || !canUseTournamentId || workingAction !== null}
                                >
                                    {workingAction === 'teams' ? 'Importuji…' : 'Importovat týmy'}
                                </Btn>
                                <Btn
                                    type="button"
                                    variant="orange"
                                    size="md"
                                    fullWidth
                                    icon={<MdEvent />}
                                    onClick={importMatches}
                                    disabled={!selectedLeagueId || !selectedSeason || !canUseTournamentId || workingAction !== null}
                                >
                                    {workingAction === 'matches' ? 'Importuji…' : 'Importovat zápasy'}
                                </Btn>
                            </div>
                        </Card>
                    </div>
                </div>

                {lastResult && (
                    <Card padding="none">
                        <div className="px-5 py-3 border-b border-border-neutral flex items-center justify-between gap-3">
                            <p className="tb-h3 text-text-primary">Poslední odpověď</p>
                            <Badge variant="success">JSON</Badge>
                        </div>
                        <pre className="max-h-[360px] overflow-auto scrollbar-thin p-5 bg-bg-inputSoft text-[12px] font-mono text-text-primary whitespace-pre-wrap">
                            {formatResult(lastResult)}
                        </pre>
                    </Card>
                )}
            </main>

            <FooterCmp />
        </>
    );
};
